import { z } from 'zod';

const uuid = z.string().uuid();

const companySchema = z.object({
  id: uuid,
  code: z.string(),
  name: z.string(),
});

const companySiteSchema = z.object({
  id: uuid,
  companyID: uuid,
  name: z.string(),
});

const documentTypeSchema = z.object({
  id: uuid,
  code: z.string(),
  name: z.string(),
});

const traderSchema = z.object({
  id: uuid,
  isFromManager: z.boolean().optional(),
  code: z.string().optional(),
  name: z.string().optional(),
  title: z.string().optional(),
  job: z.string().optional(),
  taxNumber: z.string().optional(),
  address: z.string().optional(),
  zipCode: z.string().optional(),
  city: z.string().optional(),
  doy: z.string().optional(),
  email: z.string().optional(),
});

const representativeSchema = z.object({
  id: z.string(),
  traderId: uuid,
  companyId: uuid,
  code: z.string(),
  name: z.string(),
  erpName: z.string(),
  serverAddress: z.string(),
  serverPort: z.string(),
  isFromManager: z.boolean(),
  isActive: z.boolean(),
  deleted: z.boolean(),
  trader: traderSchema,
  company: companySchema,
});

const traderSiteSchema = z
  .object({
    id: z.string(),
    createdDate: z.string(),
    lastUpdate: z.string(),
    traderId: uuid,
    code: z.string(),
    name: z.string(),
    address: z.string(),
    zipCode: z.string(),
    isFromManager: z.boolean(),
    isActive: z.boolean(),
    isHQ: z.boolean(),
    isDefault: z.boolean(),
    isBranch: z.boolean(),
    city: z.string(),
    telephone1: z.string(),
    boid: z.string(),
    deleted: z.boolean(),
    companySite: companySiteSchema,
    companySiteId: uuid,
    vatLevelId: uuid,
    vatLevel: z.object({ id: z.string(), code: z.string(), name: z.string() }).optional(),
    email: z.string(),
    county: z.string(),
  })
  .partial();

const basicCustomerSchema = z.object({
  id: z.string(),
  code: z.string(),
  profession: z.string(),
  trader: traderSchema.optional(),
});

const dcCustomerSchema = z.object({
  id: z.string(),
  isActive: z.boolean().optional(),
  deleted: z.boolean().optional(),
  isFromManager: z.boolean().optional(),
  customerId: z.string().optional(),
  customer: basicCustomerSchema.optional(),
  representativeId: z.string().optional(),
  representative: representativeSchema,
  customerRemotedCode: z.string(),
});

const paymentTypeSchema = z.object({
  id: uuid,
  isActive: z.boolean(),
  isFromManager: z.boolean(),
  boid: z.string().optional(),
  code: z.string(),
  name: z.string(),
  eType: z.number(),
});

const dcPaymentTypeSchema = z.object({
  id: uuid,
  paymentTypeId: uuid,
  representativeId: uuid,
  paymentMethodRemotedCode: z.string(),
  isActive: z.boolean(),
  paymentType: paymentTypeSchema,
  isFromManager: z.boolean(),
});

const userSchema = z
  .object({
    id: uuid,
    userName: z.string(),
    name: z.string(),
    isActive: z.boolean(),
    isFromManager: z.boolean(),
    isAdmin: z.boolean(),
    mwUserTypeID: uuid,
  })
  .partial();

// TOP-LEVEL: keep required “not-null” fields required
export const getDCDocumentHeaderByIdSchema = z.object({
  id: uuid,
  companyId: uuid,
  companySiteId: uuid,
  representativeId: uuid,
  representativeSiteId: uuid.optional(),
  representativeDocumentTypeCode: z.string(),
  deleted: z.boolean(),
  createdDate: z.string(),
  createUserId: uuid,
  isActive: z.boolean(),
  isFromManager: z.boolean(),
  detailsCount: z.number(),
  totalGrossAmount: z.number(),
  totalNetAmount: z.number(),
  totalQuantity: z.number(),
  totalVatAmount: z.number(),

  // optionals
  company: companySchema.optional(),
  companySite: companySiteSchema.optional(),

  representative: representativeSchema.optional(),

  representativeCustomerTaxCode: z.string().optional(),
  representativeCustomerAddress: z.string().optional(),
  representativeCustomerCity: z.string().optional(),
  representativeCustomerId: uuid.optional(),
  representativeCustomer: dcCustomerSchema.optional(),

  companyCustomerId: uuid.optional(),
  companyCustomer: basicCustomerSchema.optional(),

  customerSiteAddress: z.string().optional(),
  customerSiteCity: z.string().optional(),
  customerSiteId: uuid.optional(),
  customerSite: traderSiteSchema.optional(),

  representativeSite: traderSiteSchema.optional(),

  representativeRecipientTaxCode: z.string().optional(),
  representativeRecipientAddress: z.string().optional(),
  representativeRecipientCity: z.string().optional(),
  representativeRecipientId: uuid.optional(),
  representativeRecipient: dcCustomerSchema.optional(),

  companyRecipientId: uuid.optional(),
  companyRecipient: basicCustomerSchema.optional(),

  recipientSiteAddress: z.string().optional(),
  recipientSiteCity: z.string().optional(),
  recipientSiteId: uuid.optional(),
  recipientSite: traderSiteSchema.optional(),

  representativeDocumentTypeId: uuid.optional(),
  representativeDocumentType: z
    .object({
      id: uuid,
      representativeId: uuid,
      documentTypeId: z.string().nullish(),
      documentTypeRemotedCode: z.string(),
      toApprove: z.boolean(),
      importMethodType: z.number().optional(),
      exportMethodType: z.number(),
      lastExportedDate: z.string().optional(),
      exportFolder: z.string().optional(),
      exportUrl: z.string().optional(),
      exportFileNameFormat: z.string().optional(),
      documentType: z
        .object({
          id: uuid,
          isActive: z.boolean().optional(),
          isFromManager: z.boolean().optional(),
          code: z.string().optional(),
          name: z.string(),
          description: z.string().optional(),
        })
        .optional(),
      isActive: z.boolean().optional(),
      deleted: z.boolean().optional(),
      createdDate: z.string().optional(),
      createUserId: uuid.optional(),
      isFromManager: z.boolean().optional(),
      allowChange: z.boolean().optional(),
    })
    .optional(),

  companyDocumentTypeId: uuid.optional(), // object removed
  companyDocumentType: documentTypeSchema.optional(),
  representativePaymentTypeCode: z.string().optional(),
  representativePaymentTypeId: uuid.optional(),
  representativePaymentType: dcPaymentTypeSchema.optional(),
  companyPaymentTypeId: uuid.optional(), // object removed

  documentDescription: z.string().optional(),
  representativeDocumentSeriesCode: z.string().optional(),

  isApproved: z.boolean().optional(),
  approvalUserId: uuid.optional(),
  approvalUser: userSchema.optional(),

  officialDateDT: z.string().optional(),
  remarks: z.string().optional(),

  documentDetails: z.array(z.any()).optional(),

  representativeCustomerCode: z.string().optional(),
  representativeCustomerName: z.string().optional(),
  customerSiteCode: z.string().optional(),
  customerSiteName: z.string().optional(),
  representativeRecipientCode: z.string().optional(),
  representativeRecipientName: z.string().optional(),
  recipientSiteCode: z.string().optional(),
  recipientSiteName: z.string().optional(),
  documentNumber: z.string().optional(),
  isInvalid: z.boolean().optional(),
  officialDate: z.string().optional(),
});

// types
export type GetDCDocumentHeaderByIdResponse = z.output<typeof getDCDocumentHeaderByIdSchema>;
